/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { KeyRound, Smartphone, CheckCircle2, ShieldCheck, Copy } from 'lucide-react';
import { setup2FA, verify2FA, disable2FA, get2FAStatus } from '@/lib/security';

export const TwoFactorCard = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState({ enabled: false });
  const [setupState, setSetupState] = useState(null); // { qr, secret }
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setStatus(await get2FAStatus());
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
         
        console.debug('[TwoFactorCard] get2FAStatus failed', e);
      }
    }
  };
  useEffect(() => { load(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const r = await setup2FA();
      setSetupState({ qr: r.qr_code_base64, secret: r.secret });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('genericError'));
    } finally { setBusy(false); }
  };

  const confirmSetup = async () => {
    setBusy(true);
    try {
      await verify2FA(otp);
      toast.success(t('twoFAEnabledSuccess'));
      setSetupState(null); setOtp(''); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || t('genericError')); }
    finally { setBusy(false); }
  };

  const doDisable = async () => {
    setBusy(true);
    try {
      await disable2FA(otp);
      toast.success(t('twoFADisabledSuccess'));
      setOtp(''); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || t('genericError')); }
    finally { setBusy(false); }
  };

  const copySecret = () => {
    if (setupState?.secret) { navigator.clipboard.writeText(setupState.secret); toast.success(t('twoFASecretCopied')); }
  };

  return (
    <Card data-testid="two-factor-card">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="font-display flex items-center gap-2"><KeyRound className="h-5 w-5 text-primary" /> {t('twoFATitle')}</CardTitle>
            <CardDescription>{t('twoFASubtitle')}</CardDescription>
          </div>
          <Badge variant={status.enabled ? 'success' : 'outline'} data-testid="two-factor-status-badge">
            {status.enabled ? <><CheckCircle2 className="h-3 w-3 mr-1" /> {t('twoFAOn')}</> : t('twoFAOff')}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!status.enabled && !setupState && (
          <>
            <div className="rounded-2xl bg-primary/5 border border-primary/20 p-4 flex items-start gap-3">
              <Smartphone className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <div className="font-semibold">{t('twoFAWhyTitle')}</div>
                <p className="text-muted-foreground text-xs mt-1">{t('twoFAWhyDesc')}</p>
              </div>
            </div>
            <Button onClick={startSetup} disabled={busy} data-testid="two-factor-start-btn">
              {busy ? t('processing') : t('twoFAStart')}
            </Button>
          </>
        )}

        {setupState && (
          <div className="space-y-4">
            <div className="rounded-2xl border border-border/60 p-4 flex flex-col sm:flex-row items-center gap-4">
              <img src={setupState.qr} alt="2FA QR" className="h-40 w-40 rounded-xl bg-white p-2 border border-border" data-testid="two-factor-qr" />
              <div className="flex-1 text-sm space-y-2">
                <p className="font-semibold">{t('twoFAScanTitle')}</p>
                <p className="text-muted-foreground text-xs">{t('twoFAScanDesc')}</p>
                <div className="flex items-center gap-2">
                  <code className="text-xs px-2 py-1 rounded-md bg-muted flex-1 truncate select-all" data-testid="two-factor-secret">{setupState.secret}</code>
                  <Button variant="outline" size="icon" onClick={copySecret} data-testid="two-factor-copy-secret"><Copy className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="otp-input">{t('twoFAEnterCode')}</Label>
              <Input id="otp-input" inputMode="numeric" pattern="[0-9]*" maxLength={6} placeholder="123456" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} data-testid="two-factor-otp-input" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => { setSetupState(null); setOtp(''); }}>{t('cancel')}</Button>
              <Button onClick={confirmSetup} disabled={busy || otp.length !== 6} data-testid="two-factor-confirm-btn">
                {busy ? t('processing') : t('twoFAConfirm')}
              </Button>
            </div>
          </div>
        )}

        {status.enabled && !setupState && (
          <div className="space-y-3">
            <div className="rounded-2xl bg-accent/8 border border-accent/25 p-3 flex items-start gap-2" data-testid="two-factor-active-info">
              <ShieldCheck className="h-4 w-4 text-accent mt-0.5" />
              <div className="text-sm">
                <div className="font-semibold">{t('twoFAActiveTitle')}</div>
                <p className="text-muted-foreground text-xs mt-1">{t('twoFAActiveDesc')}</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="disable-otp">{t('twoFADisableCode')}</Label>
              <Input id="disable-otp" inputMode="numeric" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} data-testid="two-factor-disable-otp" />
            </div>
            <Button variant="destructive" onClick={doDisable} disabled={busy || otp.length !== 6} data-testid="two-factor-disable-btn">
              {t('twoFADisable')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TwoFactorCard;
